import { useState, useEffect } from "react";
import { Routes, Route, NavLink } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { MessagingProvider, useMessaging } from "@/context/MessagingContext";
import { OptionsProvider, useOptions } from "@/context/OptionsContext";
import api from "@/lib/api";
import { fileUrl, initials } from "@/lib/fileUrl";
import { ThemeToggle } from "@/components/ThemeToggle";
import { NotificationBell } from "@/components/NotificationBell";
import { ChatDock } from "@/components/ChatDock";
import { Button } from "@/components/ui/button";
import {
  Shield, LayoutDashboard, Users2, Map, MapPinned, Settings, LogOut, ListChecks, Menu, X, BookOpen, Network, MessageSquare, CreditCard, ScrollText, ShieldAlert, Loader2, Clock, BookMarked, CalendarDays,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

import Overview from "@/pages/dashboard/Overview";
import Leads from "@/pages/dashboard/Leads";
import AreaSearch from "@/pages/dashboard/AreaSearch";
import Team from "@/pages/dashboard/Team";
import OrgSettings from "@/pages/dashboard/OrgSettings";
import Territory from "@/pages/dashboard/Territory";
import Directory from "@/pages/dashboard/Directory";
import UserManual from "@/pages/dashboard/UserManual";
import Messages from "@/pages/dashboard/Messages";
import Billing from "@/pages/dashboard/Billing";
import Scripts from "@/pages/dashboard/Scripts";
import SuperAdmin from "@/pages/dashboard/SuperAdmin";
import BookOfBusiness from "@/pages/dashboard/BookOfBusiness";
import Planner from "@/pages/dashboard/Planner";
import { TrialBanner } from "@/components/TrialBanner";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";

const ROLE_LABEL = { admin: "Admin", manager: "Manager", agent: "Agent" };

const nav = [
  { to: "/app/superadmin", label: "Platform", icon: ShieldAlert, superadminOnly: true },
  { to: "/app", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/app/leads", label: "Leads", icon: ListChecks },
  { to: "/app/book", label: "Book of Business", icon: BookMarked },
  { to: "/app/planner", label: "Planner", icon: CalendarDays },
  { to: "/app/map", label: "Area Finder", icon: Map },
  { to: "/app/messages", label: "Messages", icon: MessageSquare },
  { to: "/app/territory", label: "Territory", icon: MapPinned },
  { to: "/app/directory", label: "Directory", icon: Network, saAllowed: true },
  { to: "/app/team", label: "Team", icon: Users2 },
  { to: "/app/scripts", label: "Scripts", icon: ScrollText, adminOnly: true },
  { to: "/app/billing", label: "Billing", icon: CreditCard, adminOnly: true },
  { to: "/app/manual", label: "User Manual", icon: BookOpen },
  { to: "/app/settings", label: "Settings", icon: Settings },
];

function Sidebar({ org, user, logout, onNavigate }) {
  const { unreadTotal } = useMessaging();
  const [renewalsDue, setRenewalsDue] = useState(0);
  useEffect(() => { api.get("/book/renewals-count").then(({ data }) => setRenewalsDue(data.due_30 || 0)).catch(() => {}); }, []);
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-start gap-2 px-6 py-5 font-heading font-bold">
        {org?.logo ? (
          <img src={fileUrl(org.logo)} alt="logo" className="mt-0.5 h-7 w-7 shrink-0 rounded object-contain" data-testid="sidebar-logo" />
        ) : (
          <Shield className="mt-0.5 h-6 w-6 shrink-0 text-primary" />
        )}
        <div className="min-w-0">
          <span className="line-clamp-2 text-base leading-tight" data-testid="sidebar-org-name">{org?.name || "IShareLeads"}</span>
          {org?.owner_id && user?.user_id !== org.owner_id && (
            <span className="mt-0.5 block truncate text-xs font-normal text-muted-foreground" data-testid="sidebar-member-name">{user.name}</span>
          )}
        </div>
      </div>
      <nav className="flex-1 space-y-1 px-3">
        {nav.filter((n) => user.role === "superadmin" ? (n.superadminOnly || n.saAllowed) : (!n.superadminOnly && (!n.adminOnly || user.role === "admin"))).map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} onClick={onNavigate}
            data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-accent hover:text-foreground"
              }`}>
            <n.icon className="h-4.5 w-4.5" /> <span className="flex-1">{n.label}</span>
            {n.to === "/app/messages" && unreadTotal > 0 && (
              <span data-testid="nav-messages-unread" className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[10px] font-bold text-primary-foreground">{unreadTotal}</span>
            )}
            {n.to === "/app/book" && renewalsDue > 0 && (
              <span data-testid="nav-renewals-due" title="Policies renewing within 30 days" className="flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1.5 text-[10px] font-bold text-white">{renewalsDue}</span>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-border p-3">
        <div className="flex items-center gap-3 rounded-lg px-3 py-2">
          <Avatar className="h-9 w-9">
            {user.picture && <AvatarImage src={fileUrl(user.picture)} alt="" />}
            <AvatarFallback className="bg-primary/15 text-primary text-xs">{initials(user.name)}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{user.name}</p>
            <p className="truncate text-xs text-muted-foreground">{ROLE_LABEL[user.role]}</p>
          </div>
        </div>
        <Button variant="ghost" className="mt-1 w-full justify-start text-muted-foreground" onClick={logout} data-testid="logout-button">
          <LogOut className="mr-2 h-4 w-4" /> Sign out
        </Button>
      </div>
    </div>
  );
}

function PendingScreen({ orgName }) {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="pending-approval-screen">
      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-500/15">
        <Clock className="h-8 w-8 text-amber-500" />
      </div>
      <h2 className="mt-6 font-heading text-2xl font-bold">Waiting for approval</h2>
      <p className="mt-2 max-w-md text-sm text-muted-foreground">
        Your request to join {orgName || "the team"} is pending. An administrator needs to approve your access before you can view leads. We'll email you the moment you're in.
      </p>
    </div>
  );
}

function DashboardInner() {
  const { user, logout } = useAuth();
  const { ready } = useOptions();
  const [open, setOpen] = useState(false);
  const [org, setOrg] = useState(null);
  useEffect(() => { api.get("/org").then(({ data }) => setOrg(data)).catch(() => {}); }, []);

  return (
    <div className="flex min-h-screen bg-background">
      <aside className="hidden w-64 shrink-0 border-r border-border bg-card lg:block">
        <Sidebar org={org} user={user} logout={logout} onNavigate={() => setOpen(false)} />
      </aside>

      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-64 border-r border-border bg-card">
            <Sidebar org={org} user={user} logout={logout} onNavigate={() => setOpen(false)} />
          </div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-40 flex items-center justify-between border-b border-border bg-background/80 px-4 py-3 glass lg:px-8">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-button">
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
            <span className="rounded-full border border-border px-3 py-1 text-xs font-medium text-muted-foreground" data-testid="org-badge">
              {user.role === "admin" ? "Admin workspace" : "Agency workspace"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <ThemeToggle />
          </div>
        </header>

        <main className="flex-1 space-y-4 p-4 lg:p-8">
          <ImpersonationBanner />
          <TrialBanner />
          {user.approval_status === "pending" ? (
            <PendingScreen orgName={org?.name} />
          ) : ready ? (
          <Routes>
            <Route index element={<Overview />} />
            <Route path="leads" element={<Leads />} />
            <Route path="book" element={<BookOfBusiness />} />
            <Route path="planner" element={<Planner />} />
            <Route path="map" element={<AreaSearch />} />
            <Route path="messages" element={<Messages />} />
            <Route path="billing" element={<Billing />} />
            <Route path="scripts" element={<Scripts />} />
            <Route path="superadmin" element={<SuperAdmin />} />
            <Route path="territory" element={<Territory />} />
            <Route path="directory" element={<Directory />} />
            <Route path="team" element={<Team />} />
            <Route path="manual" element={<UserManual />} />
            <Route path="settings" element={<OrgSettings />} />
          </Routes>
          ) : (
            <div className="flex items-center justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          )}
        </main>
      </div>

      <ChatDock />
    </div>
  );
}

export default function DashboardLayout() {
  return (
    <OptionsProvider>
      <MessagingProvider>
        <DashboardInner />
      </MessagingProvider>
    </OptionsProvider>
  );
}
